import React from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {BookOpen, Calendar, User, ArrowRight, Heart, Droplet, Users} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { analytics } from '../utils/analytics'

const blogPosts = [
  {
    id: 1,
    title: 'Por que doar sangue é importante?',
    excerpt: 'Entenda como uma única doação pode salvar até 4 vidas e por que os estoques estão sempre baixos.',
    author: 'Equipe Doe',
    date: '15 de Janeiro, 2025',
    category: 'Educação',
    image: 'https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=800&h=400&fit=crop',
    content: `
      <h2>O Impacto de Uma Doação</h2>
      <p>Cada doação de sangue pode ser fracionada em até 4 componentes diferentes: hemácias, plaquetas, plasma e crioprecipitado. Isso significa que uma única doação pode beneficiar até 4 pacientes diferentes.</p>
      
      <h3>Quem Precisa de Sangue?</h3>
      <ul>
        <li><strong>Pacientes com câncer:</strong> Tratamentos como quimioterapia destroem células sanguíneas</li>
        <li><strong>Vítimas de acidentes:</strong> Traumas graves causam perda significativa de sangue</li>
        <li><strong>Cirurgias complexas:</strong> Transplantes e operações cardíacas requerem transfusões</li>
        <li><strong>Doenças crônicas:</strong> Anemia falciforme, talassemia e outras condições</li>
      </ul>

      <h3>Por Que os Estoques Estão Baixos?</h3>
      <p>Apenas 1,6% da população brasileira doa sangue regularmente. A OMS recomenda que esse número seja de 3% a 5% para manter estoques saudáveis. Além disso, o sangue tem validade curta (35-42 dias para hemácias, 5 dias para plaquetas).</p>

      <blockquote>
        "Uma doação pode parecer pequena, mas para quem recebe, é a diferença entre a vida e a morte." - Dr. Carlos Silva, Hematologista
      </blockquote>
    `
  },
  {
    id: 2,
    title: 'Quem pode doar sangue? Requisitos completos',
    excerpt: 'Descubra se você está apto para doar e quais são os critérios de elegibilidade.',
    author: 'Dr. Ana Paula',
    date: '10 de Janeiro, 2025',
    category: 'Saúde',
    image: 'https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=800&h=400&fit=crop',
    content: `
      <h2>Requisitos Básicos para Doação</h2>
      
      <h3>✅ Você PODE doar se:</h3>
      <ul>
        <li>Tem entre 16 e 69 anos (menores de 18 precisam de autorização dos pais)</li>
        <li>Pesa mais de 50kg</li>
        <li>Está bem de saúde</li>
        <li>Dormiu pelo menos 6 horas nas últimas 24h</li>
        <li>Está alimentado (evite alimentos gordurosos 3h antes)</li>
      </ul>

      <h3>❌ Impedimentos TEMPORÁRIOS:</h3>
      <ul>
        <li><strong>Gripe/resfriado:</strong> Aguardar 7 dias após recuperação</li>
        <li><strong>Tatuagem/piercing:</strong> Aguardar 12 meses</li>
        <li><strong>Gravidez:</strong> Aguardar 90 dias após parto normal, 180 dias após cesariana</li>
        <li><strong>Vacinas:</strong> Varia de 48h a 4 semanas dependendo do tipo</li>
        <li><strong>Cirurgias:</strong> Depende da complexidade (consultar hemocentro)</li>
      </ul>

      <h3>🚫 Impedimentos DEFINITIVOS:</h3>
      <ul>
        <li>Hepatite B ou C após os 11 anos</li>
        <li>HIV/AIDS</li>
        <li>Doença de Chagas</li>
        <li>Malária (depende da região)</li>
      </ul>

      <div class="bg-blue-50 p-4 rounded-lg mt-4">
        <strong>💡 Dica:</strong> Homens podem doar a cada 60 dias (máximo 4x/ano). Mulheres a cada 90 dias (máximo 3x/ano).
      </div>
    `
  },
  {
    id: 3,
    title: 'Passo a passo: O que acontece durante a doação',
    excerpt: 'Tire todas as suas dúvidas sobre o processo de doação, desde o cadastro até a recuperação.',
    author: 'Enfermeira Maria',
    date: '5 de Janeiro, 2025',
    category: 'Processo',
    image: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?w=800&h=400&fit=crop',
    content: `
      <h2>O Processo Completo de Doação</h2>

      <h3>1️⃣ Cadastro (5-10 minutos)</h3>
      <p>Apresente documento oficial com foto. Você preencherá um formulário com histórico de saúde.</p>

      <h3>2️⃣ Pré-Triagem (10-15 minutos)</h3>
      <p>Verificação de sinais vitais: pressão arterial, temperatura, peso, pulso. Um profissional fará perguntas sobre sua saúde.</p>

      <h3>3️⃣ Teste de Hemoglobina (2 minutos)</h3>
      <p>Pequena picada no dedo para verificar se você tem anemia.</p>

      <h3>4️⃣ Triagem Clínica (5-10 minutos)</h3>
      <p>Entrevista confidencial com médico ou enfermeiro sobre comportamentos de risco.</p>

      <h3>5️⃣ Coleta de Sangue (8-12 minutos)</h3>
      <p>Você ficará deitado confortavelmente. A agulha é inserida (leve desconforto inicial). São coletados 450ml de sangue + tubos para exames.</p>

      <h3>6️⃣ Lanche e Hidratação (15-20 minutos)</h3>
      <p>Aguarde sentado, tome água/suco e coma biscoitos. Isso ajuda na recuperação.</p>

      <h3>7️⃣ Cuidados Pós-Doação</h3>
      <ul>
        <li>Mantenha o curativo por 4 horas</li>
        <li>Evite esforço físico intenso por 12 horas</li>
        <li>Beba bastante água nas próximas 24 horas</li>
        <li>Não fume por 2 horas</li>
        <li>Evite bebidas alcoólicas por 12 horas</li>
      </ul>

      <div class="bg-green-50 p-4 rounded-lg mt-4">
        <strong>⏱️ Tempo total:</strong> Aproximadamente 40-60 minutos do início ao fim.
      </div>
    `
  },
  {
    id: 4,
    title: 'Compatibilidade sanguínea: Entenda os tipos',
    excerpt: 'Descubra quem pode doar para quem e por que O- é o doador universal.',
    author: 'Dr. Roberto Lima',
    date: '28 de Dezembro, 2024',
    category: 'Educação',
    image: 'https://images.unsplash.com/photo-1584362917165-526a968579e8?w=800&h=400&fit=crop',
    content: `
      <h2>Tipos Sanguíneos e Compatibilidade</h2>

      <h3>Sistema ABO</h3>
      <p>Existem 4 tipos principais: A, B, AB e O. Cada tipo possui antígenos específicos na superfície das hemácias.</p>

      <h3>Fator Rh (+ ou -)</h3>
      <p>O fator Rh é uma proteína adicional. Quem tem é Rh+ (positivo), quem não tem é Rh- (negativo).</p>

      <h3>📊 Tabela de Compatibilidade</h3>
      
      <div class="overflow-x-auto">
        <table class="w-full border-collapse border border-gray-300 mt-4">
          <thead>
            <tr class="bg-red-100">
              <th class="border border-gray-300 p-2">Tipo</th>
              <th class="border border-gray-300 p-2">Pode Doar Para</th>
              <th class="border border-gray-300 p-2">Pode Receber De</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">A+</td>
              <td class="border border-gray-300 p-2">A+, AB+</td>
              <td class="border border-gray-300 p-2">A+, A-, O+, O-</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">A-</td>
              <td class="border border-gray-300 p-2">A+, A-, AB+, AB-</td>
              <td class="border border-gray-300 p-2">A-, O-</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">B+</td>
              <td class="border border-gray-300 p-2">B+, AB+</td>
              <td class="border border-gray-300 p-2">B+, B-, O+, O-</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">B-</td>
              <td class="border border-gray-300 p-2">B+, B-, AB+, AB-</td>
              <td class="border border-gray-300 p-2">B-, O-</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">AB+</td>
              <td class="border border-gray-300 p-2">AB+</td>
              <td class="border border-gray-300 p-2">Todos (Receptor Universal)</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">AB-</td>
              <td class="border border-gray-300 p-2">AB+, AB-</td>
              <td class="border border-gray-300 p-2">A-, B-, AB-, O-</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">O+</td>
              <td class="border border-gray-300 p-2">A+, B+, AB+, O+</td>
              <td class="border border-gray-300 p-2">O+, O-</td>
            </tr>
            <tr>
              <td class="border border-gray-300 p-2 font-bold">O-</td>
              <td class="border border-gray-300 p-2">Todos (Doador Universal)</td>
              <td class="border border-gray-300 p-2">O-</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bg-yellow-50 p-4 rounded-lg mt-4">
        <strong>⚠️ Importante:</strong> Sangue O- é o mais requisitado em emergências porque pode ser usado em qualquer pessoa. Porém, apenas 9% da população tem esse tipo.
      </div>
    `
  },
  {
    id: 5,
    title: 'Mitos e verdades sobre doação de sangue',
    excerpt: 'Desmistificamos as principais dúvidas e medos sobre doar sangue.',
    author: 'Equipe Doe',
    date: '20 de Dezembro, 2024',
    category: 'Educação',
    image: 'https://images.unsplash.com/photo-1582719471137-c3967ffb1c42?w=800&h=400&fit=crop',
    content: `
      <h2>Verdades e Mentiras sobre Doação</h2>

      <h3>❌ MITO: "Doar sangue engorda"</h3>
      <p>✅ <strong>VERDADE:</strong> Não há relação entre doação e ganho de peso. O corpo repõe o volume em 24-48h.</p>

      <h3>❌ MITO: "Dói muito"</h3>
      <p>✅ <strong>VERDADE:</strong> A picada inicial causa desconforto mínimo (como tirar sangue para exame). Durante a coleta, você não sente dor.</p>

      <h3>❌ MITO: "Posso pegar doenças"</h3>
      <p>✅ <strong>VERDADE:</strong> TODO material é descartável e estéril. É IMPOSSÍVEL contrair doenças doando sangue.</p>

      <h3>❌ MITO: "Vou passar mal"</h3>
      <p>✅ <strong>VERDADE:</strong> Reações adversas são raras (menos de 1%). Comer bem antes e hidratar-se previne mal-estar.</p>

      <h3>❌ MITO: "Meu sangue não faz diferença"</h3>
      <p>✅ <strong>VERDADE:</strong> TODA doação é crucial. Estoques ficam críticos em feriados e férias.</p>

      <h3>❌ MITO: "Só posso doar se alguém da família precisar"</h3>
      <p>✅ <strong>VERDADE:</strong> Doação é voluntária e altruísta. O sangue vai para quem precisa, independente de vínculo familiar.</p>

      <h3>✅ VERDADE: "Tatuagem impede doação temporariamente"</h3>
      <p>Sim, você deve aguardar 12 meses após fazer tatuagem ou piercing.</p>

      <h3>✅ VERDADE: "Homens e mulheres têm intervalos diferentes"</h3>
      <p>Sim. Homens: 60 dias. Mulheres: 90 dias. Isso porque mulheres perdem ferro naturalmente na menstruação.</p>

      <div class="bg-red-50 p-4 rounded-lg mt-4">
        <strong>💡 Conclusão:</strong> A maioria dos medos é infundada. Doar sangue é seguro, rápido e salva vidas!
      </div>
    `
  },
  {
    id: 6,
    title: 'Como se preparar para doar sangue',
    excerpt: 'Dicas práticas para garantir uma doação tranquila e bem-sucedida.',
    author: 'Nutricionista Clara',
    date: '15 de Dezembro, 2024',
    category: 'Dicas',
    image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800&h=400&fit=crop',
    content: `
      <h2>Preparação Ideal para Doação</h2>

      <h3>📅 No Dia Anterior</h3>
      <ul>
        <li>Durma bem (mínimo 6 horas)</li>
        <li>Hidrate-se bastante (2-3 litros de água)</li>
        <li>Evite bebidas alcoólicas</li>
        <li>Faça refeições balanceadas</li>
      </ul>

      <h3>🍽️ 3 Horas Antes</h3>
      <ul>
        <li><strong>Faça uma refeição leve:</strong> Frutas, pães integrais, sucos</li>
        <li><strong>EVITE alimentos gordurosos:</strong> Frituras, carnes gordas, queijos amarelos, leite integral</li>
        <li><strong>Por quê?</strong> Gordura no sangue (lipemia) dificulta os testes laboratoriais</li>
      </ul>

      <h3>💧 Hidratação</h3>
      <p>Beba pelo menos 500ml de água nas 2 horas antes da doação. Sangue bem hidratado flui melhor.</p>

      <h3>🚫 O Que NÃO Fazer</h3>
      <ul>
        <li>Não doe em jejum</li>
        <li>Não fume 2h antes</li>
        <li>Não pratique exercícios intensos no dia</li>
        <li>Não tome medicamentos sem orientação (consulte o hemocentro)</li>
      </ul>

      <h3>📋 Documentos Necessários</h3>
      <ul>
        <li>RG, CNH, Carteira de Trabalho ou Passaporte (original com foto)</li>
        <li>Menores de 18 anos: autorização dos pais + documento do responsável</li>
      </ul>

      <h3>👕 Roupa Adequada</h3>
      <p>Use roupas confortáveis com mangas que possam ser levantadas facilmente.</p>

      <div class="bg-green-50 p-4 rounded-lg mt-4">
        <strong>✅ Checklist Rápido:</strong><br/>
        ☐ Dormi bem<br/>
        ☐ Comi refeição leve (sem gordura)<br/>
        ☐ Bebi bastante água<br/>
        ☐ Trouxe documento com foto<br/>
        ☐ Estou me sentindo bem
      </div>
    `
  }
]

export function Blog() {
  const [selectedPost, setSelectedPost] = React.useState<typeof blogPosts[0] | null>(null)

  React.useEffect(() => {
    if (selectedPost) {
      analytics.trackBlogRead(selectedPost.title)
    }
  }, [selectedPost])

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Button
            variant="ghost"
            onClick={() => setSelectedPost(null)}
            className="mb-6"
          >
            ← Voltar para o Blog
          </Button>

          <article>
            <img
              src={selectedPost.image}
              alt={selectedPost.title}
              className="w-full h-96 object-cover rounded-2xl mb-8"
            />

            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full mb-4">
                {selectedPost.category}
              </span>
              <h1 className="text-4xl font-black text-gray-900 mb-4">
                {selectedPost.title}
              </h1>
              <div className="flex items-center text-sm text-gray-600 space-x-4">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  {selectedPost.author}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {selectedPost.date}
                </div>
              </div>
            </div>

            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
              style={{
                lineHeight: '1.8',
                color: '#374151'
              }}
            />
          </article>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section with Background Image */}
      <section 
        className="relative py-20 overflow-hidden"
        style={{
          backgroundImage: 'url(https://static.lumi.new/7c/7c67ead48dd3418862796806d75a617a.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/90 to-red-700/90 z-0"></div>
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <BookOpen className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl font-black mb-4">Blog Doe</h1>
          <p className="text-xl text-red-100 max-w-2xl mx-auto">
            Educação, dicas e informações essenciais sobre doação de sangue
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map(post => (
            <Card
              key={post.id}
              className="hover:shadow-2xl transition-shadow cursor-pointer border-l-4 border-l-red-500"
              onClick={() => setSelectedPost(post)}
            >
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <CardHeader>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-red-600 uppercase">
                    {post.category}
                  </span>
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {post.date.split(',')[0]}
                  </div>
                </div>
                <CardTitle className="text-xl line-clamp-2">
                  {post.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-gray-500">
                    <User className="h-3 w-3 mr-1" />
                    {post.author}
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600">
                    Ler mais <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-red-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <Heart className="h-12 w-12 text-red-600 mx-auto mb-4 fill-current" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Pronto para Salvar Vidas?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Agora que você está informado, encontre uma solicitação próxima ou cadastre-se como doador.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = '/solicitacoes'}
              className="bg-red-600 hover:bg-red-700"
            >
              <Droplet className="h-4 w-4 mr-2" />
              Ver Solicitações
            </Button>
            <Button
              onClick={() => window.location.href = '/sou-doador'}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <Users className="h-4 w-4 mr-2" />
              Quero Ser Doador
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
